import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ImageUpload } from "@/components/ImageUpload";

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "", username: user?.username || "", bio: user?.bio || "",
    birthday: user?.birthday ? user.birthday.slice(0, 10) : "", city: user?.city || "",
    picture: user?.picture || "",
    instagram: user?.social_links?.instagram || "", x: user?.social_links?.x || "",
    is_private: user?.is_private || false, marketing_consent: user?.marketing_consent || false,
  });
  const [survey, setSurvey] = useState({
    ilgi_alanlari: user?.gift_survey?.ilgi_alanlari || "",
    bedenler: user?.gift_survey?.bedenler || "",
    sevdigi_markalar: user?.gift_survey?.sevdigi_markalar || "",
    tarz: user?.gift_survey?.tarz || "",
    sevmedikleri: user?.gift_survey?.sevmedikleri || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put("/profile", {
        name: form.name, username: form.username, bio: form.bio,
        birthday: form.birthday || undefined, city: form.city, picture: form.picture,
        social_links: { instagram: form.instagram, x: form.x },
        is_private: form.is_private, marketing_consent: form.marketing_consent,
        gift_survey: survey,
      });
      setUser(res.data);
      toast.success(t("profile_updated"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  const exportData = async () => {
    try {
      const res = await api.get("/auth/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "hediyeniiste-verilerim.json";
      a.click();
      toast.success(t("data_exported"));
    } catch { toast.error(t("error_generic")); }
  };

  const deleteAccount = async () => {
    if (!window.confirm(t("delete_confirm_account"))) return;
    try {
      await api.delete("/auth/account");
      localStorage.removeItem("session_token");
      toast.success(t("account_deleted"));
      window.location.href = "/";
    } catch { toast.error(t("error_generic")); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="max-w-2xl" data-testid="settings-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-6">{t("settings_title")}</h1>

      <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-4 mb-6">
        <h2 className="font-heading font-semibold">{t("profile_info")}</h2>
        {user?.auth_provider === "email" && !user?.email_verified && (
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex items-center justify-between gap-3" data-testid="verify-banner">
            <p className="text-xs text-muted-foreground">{t("verify_pending")}</p>
            <Button size="sm" variant="outline" className="rounded-full text-xs shrink-0" data-testid="resend-verification-btn"
              onClick={async () => {
                try { await api.post("/auth/resend-verification"); toast.success(t("verification_sent")); }
                catch (err) { toast.error(err.response?.data?.detail || t("error_generic")); }
              }}>
              {t("send_verification")}
            </Button>
          </div>
        )}
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16 border-2 border-primary/20">
            <AvatarImage src={form.picture} />
            <AvatarFallback className="bg-secondary font-heading font-bold">{form.name?.[0]}</AvatarFallback>
          </Avatar>
          <ImageUpload aspect={1} label={t("upload_avatar")} testId="settings-avatar-upload"
            onUploaded={(url) => set("picture", url)} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>{t("lbl_name")}</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 rounded-xl" data-testid="settings-name-input" />
          </div>
          <div>
            <Label>{t("lbl_username")}</Label>
            <Input value={form.username} onChange={(e) => set("username", e.target.value)} className="mt-1 rounded-xl" data-testid="settings-username-input" />
            <p className="text-xs text-muted-foreground mt-1">{window.location.origin}/{form.username}</p>
          </div>
        </div>
        <div>
          <Label>{t("lbl_bio")}</Label>
          <Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} className="mt-1 rounded-xl" rows={2} data-testid="settings-bio-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>{t("lbl_birthday")}</Label>
            <Input type="date" value={form.birthday} onChange={(e) => set("birthday", e.target.value)} className="mt-1 rounded-xl" data-testid="settings-birthday-input" />
          </div>
          <div>
            <Label>{t("lbl_city")}</Label>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1 rounded-xl" data-testid="settings-city-input" />
          </div>
        </div>
        <div>
          <Label>{t("lbl_picture_url")}</Label>
          <Input value={form.picture} onChange={(e) => set("picture", e.target.value)} className="mt-1 rounded-xl" placeholder="https://..." data-testid="settings-picture-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Instagram</Label>
            <Input value={form.instagram} onChange={(e) => set("instagram", e.target.value)} className="mt-1 rounded-xl" placeholder="kullaniciadi" />
          </div>
          <div>
            <Label>X (Twitter)</Label>
            <Input value={form.x} onChange={(e) => set("x", e.target.value)} className="mt-1 rounded-xl" placeholder="kullaniciadi" />
          </div>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-5 mb-6">
        <h2 className="font-heading font-semibold">{t("privacy_perm")}</h2>
        <div className="flex items-center justify-between">
          <div>
            <Label>{t("private_profile")}</Label>
            <p className="text-xs text-muted-foreground">{t("private_profile_d")}</p>
          </div>
          <Switch checked={form.is_private} onCheckedChange={(v) => set("is_private", v)} data-testid="settings-private-switch" />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Label>{t("marketing")}</Label>
            <p className="text-xs text-muted-foreground">{t("marketing_d")}</p>
          </div>
          <Switch checked={form.marketing_consent} onCheckedChange={(v) => set("marketing_consent", v)} data-testid="settings-marketing-switch" />
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-4 mb-6">
        <div>
          <h2 className="font-heading font-semibold">{t("survey_title")}</h2>
          <p className="text-xs text-muted-foreground mt-1">{t("survey_help")}</p>
        </div>
        <div>
          <Label>{t("survey_interests")}</Label>
          <Input value={survey.ilgi_alanlari} onChange={(e) => setSurvey((s) => ({ ...s, ilgi_alanlari: e.target.value }))}
            className="mt-1 rounded-xl" placeholder={t("survey_interests_ph")} data-testid="survey-interests-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>{t("survey_sizes")}</Label>
            <Input value={survey.bedenler} onChange={(e) => setSurvey((s) => ({ ...s, bedenler: e.target.value }))}
              className="mt-1 rounded-xl" placeholder={t("survey_sizes_ph")} data-testid="survey-sizes-input" />
          </div>
          <div>
            <Label>{t("survey_brands")}</Label>
            <Input value={survey.sevdigi_markalar} onChange={(e) => setSurvey((s) => ({ ...s, sevdigi_markalar: e.target.value }))}
              className="mt-1 rounded-xl" placeholder={t("survey_brands_ph")} data-testid="survey-brands-input" />
          </div>
        </div>
        <div>
          <Label>{t("survey_style")}</Label>
          <Input value={survey.tarz} onChange={(e) => setSurvey((s) => ({ ...s, tarz: e.target.value }))}
            className="mt-1 rounded-xl" placeholder={t("survey_style_ph")} data-testid="survey-style-input" />
        </div>
        <div>
          <Label>{t("survey_dislikes")}</Label>
          <Input value={survey.sevmedikleri} onChange={(e) => setSurvey((s) => ({ ...s, sevmedikleri: e.target.value }))}
            className="mt-1 rounded-xl" placeholder={t("survey_dislikes_ph")} data-testid="survey-dislikes-input" />
        </div>
      </div>

      <Button onClick={save} disabled={busy} className="rounded-full w-full h-11 font-semibold mb-8" data-testid="settings-save-btn">
        {busy ? t("saving") : t("save")}
      </Button>

      <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-3">
        <h2 className="font-heading font-semibold">{t("account_kvkk")}</h2>
        <Button variant="outline" onClick={exportData} className="rounded-full w-full" data-testid="settings-export-btn">{t("export_data")}</Button>
        <Button variant="outline" onClick={async () => { await logout(); navigate("/"); }} className="rounded-full w-full" data-testid="settings-logout-btn">{t("logout")}</Button>
        <Button variant="outline" onClick={deleteAccount} className="rounded-full w-full text-destructive border-destructive/30" data-testid="settings-delete-btn">
          {t("delete_account")}
        </Button>
        <p className="text-xs text-muted-foreground text-center pt-2">
          {t("support_line")} <a href="/iletisim" className="text-primary hover:underline" data-testid="settings-support-email">{t("contact_form")}</a> · <a href="mailto:destek@hediyeniiste.com" className="text-primary hover:underline">destek@hediyeniiste.com</a>
        </p>
      </div>
    </div>
  );
}
